import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm, moduleOn } from "@/lib/perms";
import { inr } from "@/lib/format";
import { AlertTriangle, TrendingUp, Lightbulb, Info, ArrowRight } from "lucide-react";

const DOT = { CRITICAL: "bg-rose-500", HIGH: "bg-orange-500", MEDIUM: "bg-amber-500", LOW: "bg-slate-400" };
const CAT_ICON = { ATTENTION: AlertTriangle, OPPORTUNITY: Lightbulb, POSITIVE: TrendingUp, INFORMATION: Info };

export function BusinessActionsCard({ variant = "dashboard" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const entitled = moduleOn(user, "profit_intelligence") && (hasPerm(user, "expenses.view") || hasPerm(user, "reports.view"));
  const { data } = useQuery({
    queryKey: ["business-actions-card"], enabled: entitled,
    queryFn: async () => (await api.get("/business-actions")).data,
  });
  if (!entitled || !data || data.actions.length === 0) return null;
  const c = data.counts;
  const top = data.actions.slice(0, variant === "command" ? 3 : 4);
  return (
    <div data-testid="business-actions-card" className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-base font-bold text-slate-900">Business Actions</h3>
          <p className="text-xs text-slate-500" data-testid="business-actions-card-counts">
            {c.attention} need attention{c.opportunity ? ` · ${c.opportunity} opportunit${c.opportunity === 1 ? "y" : "ies"}` : ""}
          </p>
        </div>
        <button data-testid="business-actions-card-viewall" onClick={() => navigate("/business-actions")}
          className="inline-flex shrink-0 items-center gap-1 rounded-full bg-slate-900 px-3 py-1.5 text-xs font-bold text-white hover:bg-slate-800">
          View all <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="mt-3 space-y-1">
        {top.map((a) => {
          const Icon = CAT_ICON[a.category] || Info;
          return (
            <button key={a.id} data-testid={`business-actions-card-item-${a.id}`} onClick={() => navigate("/business-actions")}
              className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left hover:bg-slate-50">
              <span className={`h-2 w-2 shrink-0 rounded-full ${DOT[a.priority] || "bg-slate-300"}`} />
              <Icon className="h-4 w-4 shrink-0 text-slate-400" />
              <span className="flex-1 truncate text-sm text-slate-700">{a.title}</span>
              {a.financial_impact?.available && <span className="shrink-0 text-xs font-semibold text-slate-500">{inr(a.financial_impact.amount)}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
